import { useSyncExternalStore } from "react";

/**
 * Subscribe to a media query.
 *
 * `useSyncExternalStore` rather than `useState` + an effect because the first
 * paint matters here: the Peek renders either a side panel or a bottom sheet,
 * and a one-frame flash of the wrong one is visible on every open. This reads
 * the live value during render and never renders the wrong variant.
 *
 * The subscribe/getSnapshot pair is cached per query string so the store
 * identity is stable across renders — passing fresh closures would resubscribe
 * on every render.
 */

type QueryStore = {
  subscribe: (onChange: () => void) => () => void;
  getSnapshot: () => boolean;
};

const stores = new Map<string, QueryStore>();

function storeFor(query: string): QueryStore {
  const existing = stores.get(query);
  if (existing) {
    return existing;
  }
  const list = window.matchMedia(query);
  const store: QueryStore = {
    subscribe: (onChange) => {
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    getSnapshot: () => list.matches,
  };
  stores.set(query, store);
  return store;
}

const noop = () => () => undefined;

export function useMediaQuery(query: string): boolean {
  const store = typeof window === "undefined" ? null : storeFor(query);
  return useSyncExternalStore(
    store?.subscribe ?? noop,
    store?.getSnapshot ?? (() => false),
    // Server/prerender snapshot. The app is a SPA, but Vite's build-time
    // module evaluation still runs this file.
    () => false,
  );
}

/**
 * The rail and centred Peek start at the Tauri window's 720px minimum. Below
 * that, navigation is a bottom bar and details push in as full-screen pages.
 * Complementary queries keep a detail view from covering phone navigation
 * while the shell believes it is in desktop mode.
 */
export const SIDEBAR_QUERY = "(min-width: 720px)";
export const COMPACT_QUERY = "(max-width: 719px)";

/** True wherever navigation is the bottom bar and details fill the screen. */
export function useIsCompact(): boolean {
  return useMediaQuery(COMPACT_QUERY);
}

/**
 * True when the OS asks for reduced motion.
 *
 * index.css already flattens every transition and animation globally, so this
 * is only needed where motion is driven from JavaScript — the sheet's
 * drag-to-dismiss spring, which no stylesheet can reach.
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}
